// Settings → General: the Meta Ads card (Marketing Phase B). Account id,
// token presence (never the token), last sync, the nightly switch (the
// `meta_ads_sync` automation), Test connection / Sync now, and the ad set →
// strategy mapping. Mockup: docs/plans/feedback-round-three/marketing/Additions.tsx.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { SelectField, formatMoney } from '../../components/primitives';
import { ApiError, api } from '../../lib/api';
import { metaErrorText, metaSyncState, metaWhen, plural, syncConflictText } from '../../lib/marketing';
import type { MarketingMetaResponse, MetaAdsStatus, MetaSyncResult } from '../../../shared/marketing';

type Notice = { tone: 'ok' | 'bad'; text: string } | null;
type TestResult = { ok: true; name: string; currency: string } | { ok: false; code: MetaSyncResult['code'] };

const NONE = '__none';

export function MetaAdsCardView({ data, mobile, busy, notice, account, rowErrors, onAccount, onSaveAccount, onNightly, onSync, onTest, onMap }: {
  data: MarketingMetaResponse;
  mobile: boolean;
  busy: 'sync' | 'test' | 'account' | 'nightly' | null;
  notice: Notice;
  account: string;
  rowErrors: Record<string, string>;
  onAccount: (v: string) => void;
  onSaveAccount: () => void;
  onNightly: (enabled: boolean) => void;
  onSync: () => void;
  onTest: () => void;
  onMap: (adSetId: string, strategyId: string | null) => void;
}) {
  const s = data.status;
  const state = metaSyncState(s);
  const r = s.last_result;
  const saved = s.account_id ? `act_${s.account_id}` : '';
  const dirty = account.trim() !== saved;
  const unmapped = data.ad_sets.filter((a) => !a.strategy_id && a.spend_7d_cents > 0).length;
  const items = [
    { value: NONE, label: 'Not mapped' },
    ...data.strategies.map((st) => ({ value: st.id, label: st.name, sub: st.channel_name ?? 'No channel', group: st.channel_name ?? 'No channel' })),
  ];
  const btn = mobile ? 'cbtn' : 'btn';
  const submit = (e: FormEvent) => { e.preventDefault(); if (dirty) onSaveAccount(); };

  return (
    <div className="settings-card mk-meta-card" id="meta-ads">
      <div className="sc-head">
        <span className="sc-title">Meta Ads</span>
        <span className={`sc-meta${state.tone === 'ok' ? ' on' : state.tone === 'bad' ? ' bad' : ''}`}>{state.label}</span>
      </div>
      <div className="sc-desc">Pulls spend, impressions, reach, clicks and leads for each ad set into Marketing once a day (03:00 UTC, early afternoon in Melbourne). Read-only — SS OS never changes your ads.</div>

      <form className="sc-row mk-sc-account" onSubmit={submit}>
        <label htmlFor="meta-account">Ad account</label>
        <span className="meta">
          <input id="meta-account" className="f-input mono" value={account} placeholder="act_1234567890" spellCheck={false} autoComplete="off"
            disabled={busy === 'account'} onChange={(e) => onAccount(e.target.value)} />
          {dirty && <button className={`${btn} primary`} type="submit" disabled={busy === 'account'}>{busy === 'account' ? 'Saving…' : 'Save'}</button>}
        </span>
      </form>
      <div className="sc-row">
        <span>Access token</span>
        <span className="meta">{s.token_present ? <span className="mk-sc-ok">Set</span> : <span className="mk-sc-bad">Not set</span>} · Worker secret</span>
      </div>
      <div className="mk-sc-hint">
        {s.token_present ? 'Stored as an encrypted Worker secret; SS OS never shows it. Replace it' : 'Add a system-user token'} from a terminal with <code>wrangler secret put META_ADS_ACCESS_TOKEN</code>.
      </div>
      <div className="sc-row">
        <span>Last success</span>
        <span className="meta">
          {s.last_sync_at ? metaWhen(s.last_sync_at) : 'Never'}
          {r?.ok && r.ad_sets !== undefined ? ` · ${plural(r.ad_sets, 'ad set')}` : ''}
          {r?.ok && r.rows !== undefined ? ` · ${plural(r.rows, 'row')}` : ''}
        </span>
      </div>
      {r && !r.ok && (
        <div className="sc-row mk-sc-err">
          <span>Last attempt</span>
          <span className="meta"><span className="mk-sc-bad">{metaWhen(r.at)} · {metaErrorText(r.code)}</span>{data.nightly_enabled ? ' · retries tonight' : ''}</span>
        </div>
      )}
      <div className="sc-row">
        <span>Nightly sync</span>
        <span className="meta">
          <button type="button" className={`atm-switch${data.nightly_enabled ? ' on' : ''}`} role="switch" aria-checked={data.nightly_enabled}
            aria-label="Nightly sync" disabled={busy === 'nightly'} onClick={() => onNightly(!data.nightly_enabled)} />
        </span>
      </div>
      <div className="mk-sc-actions">
        <button className={btn} disabled={busy !== null} onClick={onSync}>{busy === 'sync' ? 'Syncing…' : 'Sync now'}</button>
        <button className={btn} disabled={busy !== null} onClick={onTest}>{busy === 'test' ? 'Testing…' : 'Test connection'}</button>
        {notice && <span className={`mk-sc-notice ${notice.tone === 'ok' ? 'mk-sc-ok' : 'mk-sc-bad'}`} role="status">{notice.text}</span>}
      </div>

      <div className="sc-head mk-sc-sub">
        <span className="sc-title">Ad sets → strategies</span>
        {data.ad_sets.length > 0 && <span className={`sc-meta${unmapped ? ' warn' : ' on'}`}>{unmapped ? `${unmapped} unmapped` : 'All mapped'}</span>}
      </div>
      <div className="sc-desc">Each Meta ad set feeds one strategy. Spend from an unmapped ad set isn’t counted in any strategy until you map it.</div>
      {data.ad_sets.length === 0 && <div className="mk-sc-hint">No ad sets yet — they appear here after the first sync.</div>}
      {data.ad_sets.map((a) => {
        const flagged = !a.strategy_id && a.spend_7d_cents > 0;
        return (
          <div key={a.id} id={`meta-ad-set-${a.id}`} className={`mk-map-row${flagged ? ' flagged' : ''}`}>
            <div className="mk-map-l">
              <span className="mk-map-name">{a.name}</span>
              <span className="mk-map-sub">
                {[a.status ? a.status.charAt(0) + a.status.slice(1).toLowerCase() : null, `${formatMoney(a.spend_30d_cents)} last 30 days`].filter(Boolean).join(' · ')}
                {flagged && <span className="mk-sc-warn"> · spending, not mapped</span>}
              </span>
              {rowErrors[a.id] && <span className="mk-sc-bad mk-map-err" role="alert">{rowErrors[a.id]}</span>}
            </div>
            <div className="mk-map-r">
              <SelectField items={items} value={a.strategy_id ?? NONE} searchable label="Strategy"
                onChange={(v) => onMap(a.id, v === NONE ? null : v)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MetaAdsCard({ mobile }: { mobile: boolean }) {
  const [data, setData] = useState<MarketingMetaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState('');
  const [busy, setBusy] = useState<'sync' | 'test' | 'account' | 'nightly' | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const { hash } = useLocation();

  const load = useCallback(async () => {
    try {
      const res = await api<MarketingMetaResponse>('/api/admin/marketing/meta');
      setData(res);
      setError(null);
      return res;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load Meta Ads.');
      return null;
    }
  }, []);

  useEffect(() => {
    void load().then((res) => { if (res) setAccount(res.status.account_id ? `act_${res.status.account_id}` : ''); });
  }, [load]);

  // Deep links from Marketing (#meta-ads, #meta-ad-set-<id>) land once loaded.
  const loaded = data !== null;
  useEffect(() => {
    if (!loaded || !hash.startsWith('#meta-')) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'center' });
  }, [loaded, hash]);

  if (error && !data) return <div className="settings-card mk-meta-card" id="meta-ads"><div className="sc-head"><span className="sc-title">Meta Ads</span></div><div className="error-text" role="alert">{error}</div></div>;
  if (!data) return <div className="settings-card mk-meta-card" id="meta-ads"><div className="sc-head"><span className="sc-title">Meta Ads</span><span className="sc-meta">…</span></div></div>;

  const setStatus = (status: MetaAdsStatus) => setData((d) => (d ? { ...d, status } : d));

  const saveAccount = async () => {
    setBusy('account'); setNotice(null);
    try {
      const status = await api<MetaAdsStatus>('/api/admin/marketing/meta/account', { method: 'PUT', json: { account_id: account.trim() || null } });
      setStatus(status);
      setAccount(status.account_id ? `act_${status.account_id}` : '');
      setNotice({ tone: 'ok', text: status.account_id ? 'Ad account saved.' : 'Ad account cleared.' });
    } catch (e) {
      setNotice({ tone: 'bad', text: e instanceof ApiError && e.status === 400 ? 'Use the ad account ID — digits, with or without act_.' : 'Could not save the ad account.' });
    }
    setBusy(null);
  };

  const nightly = async (enabled: boolean) => {
    setBusy('nightly');
    setData((d) => (d ? { ...d, nightly_enabled: enabled } : d));
    try {
      await api('/api/admin/automations/meta_ads_sync', { method: 'PATCH', json: { enabled } });
    } catch {
      setData((d) => (d ? { ...d, nightly_enabled: !enabled } : d));
      setNotice({ tone: 'bad', text: 'Could not change the nightly sync.' });
    }
    setBusy(null);
  };

  const sync = async () => {
    setBusy('sync'); setNotice(null);
    try {
      const r = await api<MetaSyncResult>('/api/admin/marketing/meta/sync', { method: 'POST' });
      setNotice(r.ok
        ? { tone: 'ok', text: `Synced ${plural(r.ad_sets ?? 0, 'ad set')} · ${plural(r.rows ?? 0, 'row')}.` }
        : { tone: 'bad', text: `${metaErrorText(r.code)}.` });
      await load();
    } catch (e) {
      setNotice({ tone: 'bad', text: e instanceof ApiError && e.status === 409 ? `${syncConflictText(e.message)}.` : 'Sync failed — try again.' });
    }
    setBusy(null);
  };

  const test = async () => {
    setBusy('test'); setNotice(null);
    try {
      const r = await api<TestResult>('/api/admin/marketing/meta/test', { method: 'POST' });
      setNotice(r.ok
        ? { tone: 'ok', text: `Connected to ${r.name || 'the ad account'}${r.currency ? ` · ${r.currency}` : ''}.` }
        : { tone: 'bad', text: `${metaErrorText(r.code)}.` });
    } catch {
      setNotice({ tone: 'bad', text: 'Could not reach SS OS — try again.' });
    }
    setBusy(null);
  };

  const map = async (adSetId: string, strategyId: string | null) => {
    const before = data.ad_sets;
    const name = strategyId ? data.strategies.find((st) => st.id === strategyId)?.name ?? null : null;
    setRowErrors(({ [adSetId]: _drop, ...rest }) => rest);
    // Optimistic: a strategy feeds one ad set, so moving it clears the old row.
    setData((d) => d && { ...d, ad_sets: d.ad_sets.map((a) => a.id === adSetId ? { ...a, strategy_id: strategyId, strategy_name: name } : a) });
    try {
      await api(`/api/admin/marketing/meta/ad-sets/${adSetId}/strategy`, { method: 'PUT', json: { strategy_id: strategyId } });
      await load();
    } catch (e) {
      setData((d) => d && { ...d, ad_sets: before });
      const text = e instanceof ApiError && e.status === 409 ? 'That strategy already has an ad set — unmap it there first.'
        : e instanceof ApiError && e.status === 404 ? 'That strategy no longer exists.' : 'Could not save the mapping.';
      setRowErrors((old) => ({ ...old, [adSetId]: text }));
    }
  };

  return (
    <MetaAdsCardView data={data} mobile={mobile} busy={busy} notice={notice} account={account} rowErrors={rowErrors}
      onAccount={(v) => { setAccount(v); setNotice(null); }} onSaveAccount={() => void saveAccount()} onNightly={(v) => void nightly(v)}
      onSync={() => void sync()} onTest={() => void test()} onMap={(a, st) => void map(a, st)} />
  );
}
